import { useState } from 'react';
import {
  BookOpen,
  CalendarCheck,
  CreditCard,
  GraduationCap,
  Bus,
  Library,
  Layers,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { activityApi } from '@/features/activity/api/activity.api';
import { USAGE_PERIODS, type UsagePeriod } from '@/features/activity/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { UsageWindow } from '@/types/usage';

const TOP = 6;

const FEATURE_ICONS: Record<string, any> = {
  attendance: CalendarCheck,
  homework: BookOpen,
  fees: CreditCard,
  students: GraduationCap,
  transport: Bus,
  library: Library,
};

const BADGE_COLORS = [
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400',
  'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-400',
  'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-400',
  'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400',
  'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-400',
];

export function TopFeaturesPanel() {
  const [period, setPeriod] = useState<UsagePeriod>('30');
  const [showAll, setShowAll] = useState(false);
  const days = Number(period) as UsageWindow;

  const { data, isLoading, error, refetch } = useApiQuery(
    () => activityApi.featureUsage(days),
    [days],
  );

  const rows = data ? (showAll ? data.features : data.features.slice(0, TOP)) : [];

  return (
    <section
      className="flex flex-col justify-between rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
      aria-labelledby="top-features-heading"
    >
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2
              id="top-features-heading"
              className="text-[14.5px] font-bold text-slate-800 dark:text-slate-100 tracking-tight"
            >
              Most used features
            </h2>
            <p className="mt-0.5 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">
              Ranked by how many schools used each one
            </p>
          </div>
          <Tabs
            options={USAGE_PERIODS}
            value={period}
            onChange={setPeriod}
            label="Feature use period"
            variant="segmented"
          />
        </div>

        {isLoading && <SkeletonLines count={5} />}

        {!isLoading && error && (
          <QueryError title="Could not load feature use" error={error} onRetry={refetch} />
        )}

        {!isLoading && !error && data && (
          <div className="mt-4 flex flex-col gap-2">
            {rows.map((feature, index) => {
              const Icon = FEATURE_ICONS[feature.key.toLowerCase()] || Layers;
              const badgeColor = BADGE_COLORS[index % BADGE_COLORS.length];
              const percentage =
                data.totalSchools === 0
                  ? 0
                  : Math.round((feature.schools / data.totalSchools) * 100);

              return (
                <div
                  key={feature.key}
                  className="flex items-center justify-between gap-2.5 py-1.5 px-2 rounded-xl transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40"
                >
                  {/* Left: Rank & Feature Tag */}
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={cn(
                        'grid h-6 w-6 shrink-0 place-items-center rounded-md text-[11.5px] font-black tabular-nums',
                        badgeColor,
                      )}
                    >
                      {index + 1}.
                    </span>

                    <div className="flex w-32 sm:w-36 shrink-0 items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 px-2 py-1 border border-slate-200/60 dark:border-slate-700">
                      <Icon className="h-3.5 w-3.5 text-[#5b52f9] shrink-0" />
                      <span className="truncate text-[12px] font-bold text-slate-800 dark:text-slate-200">
                        {feature.name}
                      </span>
                    </div>
                  </div>

                  {/* Center: Dynamic Progress Bar */}
                  <div className="flex-1 min-w-0 mx-2 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-indigo-300 to-indigo-400 dark:from-indigo-500 dark:to-indigo-400 transition-all duration-700 ease-out"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>

                  {/* Right: Counts & % */}
                  <div className="flex items-center gap-1.5 text-right shrink-0">
                    {/* Schools out of schools — the same two numbers the % is
                        drawn from, so the pair can never disagree. */}
                    <span
                      className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 tabular-nums"
                      title={`${formatNumber(feature.actions)} ${feature.unit} in this period`}
                    >
                      {feature.schools}/{data.totalSchools}
                    </span>
                    <span className="w-8 text-[12px] font-black text-slate-800 dark:text-slate-100 tabular-nums text-right">
                      {percentage}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
        <Link
          to="/platform/catalogue"
          className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#5b52f9] hover:underline transition-colors group"
        >
          <span>Feature catalogue</span>
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
        {data && data.features.length > TOP && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAll((current) => !current)}
            className="text-[11.5px] font-bold h-7 px-2.5"
          >
            {showAll ? 'Show top 6' : `Show all ${data.features.length}`}
          </Button>
        )}
      </div>
    </section>
  );
}
